// Content for /cost-guides (M6), signed off in the open-items review, item 1.
//
// The mockup's version of this page was a pricing authority: tight ranges
// under a "Real Prices. Real Data." banner, plus a tonnage-by-tonnage
// replacement table. That is out, and deliberately so. Ask-HVAC.com does
// not survey contractors, does not hold a pricing dataset, and has no
// business implying what a contractor ought to charge.
//
// What replaces it is broad and honest: wide educational ranges for the
// common repairs, and ONE planning range for a whole-system replacement
// with the three things that actually move the number. Every range on this
// page is wide on purpose — a narrow range reads as a quote, and a
// homeowner who walks into an appointment holding a number we invented is
// worse off than one holding none.
//
// RULE FOR ANYONE EDITING THIS FILE: no figure here may be presented as a
// quote, a standard price, or a fair-price ceiling. If a range ever gets
// narrowed, it needs a real source and a date printed beside it.

export interface RepairCost {
    id: string
    name: string
    low: number
    high: number
    /** What the part does and why it fails — the education, not the price. */
    what: string
    /** What legitimately moves this particular job's price within the range. */
    drivers: string
}

/**
 * Common repairs, as broad educational ranges.
 *
 * The spreads are wide because the real ones are. A condenser fan motor is
 * a stocked universal part on a 12-year-old builder-grade unit and a
 * brand-specific ECM with a control module on a modern variable-speed
 * system — same repair name, an order of magnitude apart in parts cost.
 */
export const REPAIRS: RepairCost[] = [
    {
        id: 'capacitor',
        name: 'Capacitor replacement',
        low: 125,
        high: 450,
        what: 'A capacitor stores the jolt of energy that gets the compressor and fan motors spinning. They are consumables — heat degrades them, and Florida heat degrades them faster. A failed capacitor is one of the most common reasons an outdoor unit hums but will not start.',
        drivers: 'Whether it is a single or dual-run capacitor, the part rating, whether the visit is a scheduled call or after-hours emergency, and whether a diagnostic fee is separate or folded in.',
    },
    {
        id: 'contactor',
        name: 'Contactor replacement',
        low: 175,
        high: 400,
        what: 'The contactor is the heavy-duty relay that switches power to the outdoor unit when the thermostat calls for cooling. Its contacts pit and burn over years of switching, and insects are drawn to the coil — a stuck or burned contactor leaves the unit either dead or unable to shut off.',
        drivers: 'Part availability, whether the contactor alone failed or took the capacitor with it, and access to the electrical compartment.',
    },
    {
        id: 'condenser-fan-motor',
        name: 'Condenser fan motor',
        low: 350,
        high: 2875,
        what: 'The motor turning the fan on top of the outdoor unit, which rejects the heat your system pulled out of the house. When it fails, the system loses its ability to shed heat — pressures climb, the compressor labours, and cooling falls off or stops.',
        drivers: 'This is the widest range on the page for a reason: a universal replacement motor on an older single-stage unit sits near the bottom, while a brand-specific ECM motor with its own control module on a modern high-efficiency system sits near the top. Whether a matching blade and capacitor are needed also moves it.',
    },
    {
        id: 'blower-motor',
        name: 'Indoor blower motor',
        low: 450,
        high: 3000,
        what: 'The motor that moves conditioned air through your ductwork. A failing blower shows up as weak airflow, unusual noise, or a system that runs while barely cooling — and it is often the downstream casualty of months of restricted airflow from a neglected filter.',
        drivers: 'Same split as the condenser motor: a PSC motor versus a variable-speed ECM with a module is most of the range. Attic and crawlspace air handlers add labour, and some units require pulling the blower assembly to reach it.',
    },
    {
        id: 'refrigerant',
        name: 'Refrigerant leak repair and recharge',
        low: 600,
        high: 2800,
        what: 'Refrigerant is not consumed — a system low on refrigerant has a leak, and topping it up without finding that leak is money poured into the atmosphere. Proper work means leak detection, the repair itself, then evacuating and weighing in a correct charge.',
        drivers: 'Which refrigerant the system uses is the single biggest factor: R-22 is out of production and priced accordingly, R-410A is stepping down under the AIM Act, and current systems use R-32 or R-454B. After that: how long detection takes, where the leak is, and how many pounds the system holds.',
    },
    {
        id: 'evaporator-coil',
        name: 'Evaporator coil replacement',
        low: 1500,
        high: 4750,
        what: 'The indoor coil where refrigerant absorbs heat from your air. Coils fail through formicary corrosion — pinhole leaks that develop over years — and a leaking coil is usually the point where the repair-or-replace conversation becomes serious, because the cost approaches a meaningful fraction of a new system.',
        drivers: 'Cased versus uncased coils, whether the coil is still under a parts warranty, refrigerant type and charge, access to the air handler, and whether the coil is even still manufactured for that system.',
    },
]

/**
 * The whole-system replacement planning range.
 *
 * ONE range, not a tonnage table and not a tier table (open-items review,
 * item 1 — explicitly declined). A homeowner planning a replacement needs
 * to know the size of the decision, not a false precision about their
 * specific configuration that only a site visit can establish.
 */
export const REPLACEMENT_RANGE = { low: 8500, high: 25000 }

export interface PriceFactor {
    n: number
    title: string
    body: string
    examples: string[]
}

/** The three things that actually decide where in the range a job lands. */
export const REPLACEMENT_FACTORS: PriceFactor[] = [
    {
        n: 1,
        title: 'System size and equipment tier',
        body: 'How much capacity your house needs, and which grade of equipment you choose to meet it. This is usually the largest single share of the price, and it is the part most within your control at the quoting stage.',
        examples: [
            'Tonnage — the capacity the house requires, established by a load calculation rather than by copying the old unit forward.',
            'Efficiency rating — a higher SEER2 unit costs more up front and less to run.',
            'Equipment technology — entry-level single-stage, higher-efficiency, variable-capacity, or premium communicating systems.',
        ],
    },
    {
        n: 2,
        title: 'Options and upgrades you select',
        body: 'Everything added to the base system. None of these are required for a system to work correctly, and several are genuinely worth having — but each one is a line on the quote, and they add up faster than most homeowners expect.',
        examples: [
            'UV systems, air purification and upgraded filtration.',
            'Indoor air quality products and humidity-control equipment.',
            'Thermostats, controls and other accessories.',
        ],
    },
    {
        n: 3,
        title: 'Installation difficulty',
        body: 'The same equipment costs very different amounts to install depending on what the installer has to work with. A straightforward garage change-out and a tight attic replacement in August are not the same job, and a quote that reflects that is not a quote being padded.',
        examples: [
            'Access — a garage or closet installation versus an attic or crawlspace.',
            'Ductwork condition, sizing and any modifications needed.',
            'Electrical work, drain lines, condensate routing and platforms.',
            'Refrigerant line sets, and whether existing lines can be reused.',
        ],
    },
]

/**
 * The disclaimer that must appear anywhere on this site a dollar figure does.
 *
 * Exported rather than typed inline so the cost page, the repair-or-replace
 * calculator and anything added later cannot drift apart on it.
 */
export const PRICE_DISCLAIMER =
    'These figures are general educational estimates only. They are not quotes, not guaranteed industry prices, and not a statement of what any contractor should charge. Actual prices vary by contractor, equipment, parts, labor, accessibility, geographic area, and the specifics of the repair. Only an on-site evaluation by a qualified HVAC professional can produce a real price for your home.'

/** The questions worth asking about any quote, whatever the number on it. */
export const QUOTE_QUESTIONS = [
    'Is the diagnostic fee applied to the repair if I go ahead?',
    'What warranty covers this part, and what covers the labour to fit it?',
    'For a replacement: can I see the load calculation for my house?',
    'What is the total installed price, including permits, and what is optional on this quote?',
    'If this repair does not fix it, what is the next step and what does it cost?',
]
